import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Divider,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import type { Booking, Product } from "../../types/booking";
import type { SendcloudShipment } from "../../types/sendcloud";
import { AIR_FREIGHT } from "../../lib/pricingDefaults";
import {
  calculateChargeableWeight,
  getBookingProducts,
} from "./invoiceFormHelpers";
import { sendcloudService } from "../../services/sendcloudService";
import { supabase } from "../../lib/supabase";

const { Text } = Typography;

function productChargeableKg(product: Product): number {
  const weight = Number(product.gross_weight || 0);
  const volumetric =
    Number(product.cbm || 0) * AIR_FREIGHT.VOLUMETRIC_FACTOR;
  return Math.max(weight, volumetric);
}

export type CostOverviewDetailKind = "products" | "inklaring" | "shipments";

interface InvoiceCostOverviewDetailsProps {
  kind: CostOverviewDetailKind | null;
  booking: Booking | null;
  onClose: () => void;
  /** After HS codes are saved on booking products (inklaring modal). */
  onProductsHsUpdated?: (products: Product[]) => void | Promise<void>;
}

function formatWeightGrams(weightInGrams: number | null): string {
  if (!weightInGrams) return "N/A";
  if (weightInGrams >= 1000) return `${(weightInGrams / 1000).toFixed(2)} kg`;
  return `${weightInGrams} g`;
}

function formatDimensions(
  length: number | null,
  width: number | null,
  height: number | null,
): string {
  if (!length || !width || !height) return "N/A";
  return `${length}×${width}×${height} cm`;
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    created: "blue",
    pending: "orange",
    ready_to_send: "cyan",
    sent_to_carrier: "purple",
    "driver-on-route": "geekblue",
    delivered: "green",
    cancelled: "red",
    exception: "red",
  };
  return colors[status.toLowerCase()] || "default";
}

function normalizeHsCode(raw: string): string | null {
  const digits = raw.replace(/\D/g, "");
  return digits.length > 0 ? digits : null;
}

/** Plain-text list of products without HS code, suitable for pasting into an email. */
function formatMissingHsCodesForEmail(
  products: Product[],
  booking: Booking
): string {
  const missing = products.filter((p) => !normalizeHsCode(String(p.hs_code ?? "")));
  const reference = booking.reference || `EFF${booking.id}`;
  const lines = missing.map((p) => {
    const qtyBits = [
      p.pieces != null ? `${p.pieces} pcs` : null,
      p.cartons != null ? `${p.cartons} cartons` : null,
    ].filter(Boolean);
    return qtyBits.length > 0
      ? `- ${p.name} (${qtyBits.join(" / ")})`
      : `- ${p.name}`;
  });

  return [
    `Booking ${reference} — products missing an HS (customs) code:`,
    "",
    ...lines,
    "",
    "Please provide the HS code for each of the products listed above.",
  ].join("\n");
}

const TITLE: Record<CostOverviewDetailKind, string> = {
  products: "Products (tariff basis)",
  inklaring: "Products — Inklaringskosten",
  shipments: "Outbound shipments",
};

const summaryBoxStyle: React.CSSProperties = {
  marginTop: 12,
  padding: "10px 12px",
  background: "#fafafa",
  border: "1px solid #f0f0f0",
  borderRadius: 6,
  display: "flex",
  flexWrap: "wrap",
  gap: "8px 20px",
};

const HsCodeEditor: React.FC<{
  product: Product;
  saving: boolean;
  onSave: (productId: number, hsCode: string | null) => Promise<void>;
}> = ({ product, saving, onSave }) => {
  const [draft, setDraft] = useState(product.hs_code ? String(product.hs_code) : "");

  useEffect(() => {
    setDraft(product.hs_code ? String(product.hs_code) : "");
  }, [product.id, product.hs_code]);

  const commit = async () => {
    const next = normalizeHsCode(draft);
    const current = product.hs_code ? String(product.hs_code).replace(/\D/g, "") : null;
    if (next === current) {
      setDraft(next ?? "");
      return;
    }
    await onSave(product.id, next);
  };

  const missing = !normalizeHsCode(draft);

  return (
    <Input
      size="small"
      value={draft}
      disabled={saving}
      status={missing ? "warning" : undefined}
      placeholder="HS code"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onPressEnter={(e) => {
        (e.target as HTMLInputElement).blur();
      }}
      style={{ maxWidth: 140 }}
    />
  );
};

const InvoiceCostOverviewDetails: React.FC<InvoiceCostOverviewDetailsProps> = ({
  kind,
  booking,
  onClose,
  onProductsHsUpdated,
}) => {
  const [shipments, setShipments] = useState<SendcloudShipment[]>([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [localProducts, setLocalProducts] = useState<Product[]>([]);
  const [savingProductId, setSavingProductId] = useState<number | null>(null);

  useEffect(() => {
    setLocalProducts(booking ? getBookingProducts(booking) : []);
  }, [booking, kind]);

  const products = kind === "inklaring" ? localProducts : booking ? getBookingProducts(booking) : [];
  const uniqueHsCodes = useMemo(
    () => [...new Set(products.map((p) => p.hs_code).filter(Boolean).map(String))],
    [products],
  );
  const uniqueHs = uniqueHsCodes.length;
  const missingHsCount = products.filter(
    (p) => !normalizeHsCode(String(p.hs_code ?? "")),
  ).length;

  useEffect(() => {
    if (kind !== "shipments" || !booking) {
      setShipments([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingShipments(true);
      try {
        const data = await sendcloudService.getShipmentsForBooking(booking.id);
        if (!cancelled) setShipments(data.filter((s) => s.status !== "cancelled"));
      } catch (err) {
        console.error("Error fetching shipments for cost overview:", err);
        if (!cancelled) setShipments([]);
      } finally {
        if (!cancelled) setLoadingShipments(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, booking]);

  const saveHsCode = async (productId: number, hsCode: string | null) => {
    setSavingProductId(productId);
    try {
      const { error } = await supabase
        .from("booking_product")
        .update({ hs_code: hsCode })
        .eq("id", productId);
      if (error) throw error;

      const updated = localProducts.map((p) =>
        p.id === productId ? { ...p, hs_code: hsCode } : p,
      );
      setLocalProducts(updated);
      await onProductsHsUpdated?.(updated);
      message.success(hsCode ? "HS code saved" : "HS code cleared");
    } catch (err) {
      console.error(err);
      message.error(err instanceof Error ? err.message : "Failed to save HS code");
    } finally {
      setSavingProductId(null);
    }
  };

  const productColumns =
    kind === "inklaring"
      ? [
          { title: "Name", dataIndex: "name", key: "name", ellipsis: true },
          {
            title: "HS Code",
            dataIndex: "hs_code",
            key: "hs_code",
            width: 160,
            render: (_: unknown, row: Product) => (
              <HsCodeEditor
                product={row}
                saving={savingProductId === row.id}
                onSave={saveHsCode}
              />
            ),
          },
          {
            title: "Pieces",
            dataIndex: "pieces",
            key: "pieces",
            width: 70,
            align: "right" as const,
            render: (v: number | null | undefined) => v ?? 0,
          },
          {
            title: "Cartons",
            dataIndex: "cartons",
            key: "cartons",
            width: 80,
            align: "right" as const,
            render: (v: number | null | undefined) => v ?? 0,
          },
        ]
      : [
          { title: "Name", dataIndex: "name", key: "name", ellipsis: true },
          {
            title: "Pieces",
            dataIndex: "pieces",
            key: "pieces",
            width: 70,
            align: "right" as const,
            render: (v: number | null | undefined) => v ?? 0,
          },
          {
            title: "Cartons",
            dataIndex: "cartons",
            key: "cartons",
            width: 80,
            align: "right" as const,
            render: (v: number | null | undefined) => v ?? 0,
          },
          {
            title: "Weight",
            dataIndex: "gross_weight",
            key: "gross_weight",
            width: 90,
            align: "right" as const,
            render: (v: number) => `${Number(v || 0).toFixed(2)} kg`,
          },
          {
            title: "CBM",
            dataIndex: "cbm",
            key: "cbm",
            width: 70,
            align: "right" as const,
            render: (v: number) => Number(v || 0).toFixed(3),
          },
          {
            title: (
              <span>
                Chargeable{" "}
                <Tooltip title="Higher of actual weight and volumetric weight (1 CBM = 167 kg)">
                  <InfoCircleOutlined style={{ fontSize: 11 }} />
                </Tooltip>
              </span>
            ),
            key: "chargeable",
            width: 110,
            align: "right" as const,
            render: (_: unknown, row: Product) =>
              `${productChargeableKg(row).toFixed(2)} kg`,
          },
        ];

  const shipmentColumns = [
    {
      title: "Shipment ID",
      dataIndex: "sendcloud_shipment_id",
      key: "sendcloud_shipment_id",
      width: 110,
      render: (id: string) => (
        <Text code style={{ fontSize: 11 }}>
          {id}
        </Text>
      ),
    },
    {
      title: "Tracking",
      key: "tracking",
      render: (_: unknown, record: SendcloudShipment) => (
        <div>
          <Text strong style={{ fontSize: 12 }}>
            {record.tracking_number || "—"}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 10 }}>
            {record.carrier_name || "Unknown"}
            {record.shipping_method ? ` · ${record.shipping_method}` : ""}
          </Text>
        </div>
      ),
    },
    {
      title: "Destination",
      key: "destination",
      render: (_: unknown, record: SendcloudShipment) => (
        <div>
          <Text style={{ fontSize: 11 }}>
            {record.to_company_name || record.to_name}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 10 }}>
            {record.to_city}, {record.to_country}
          </Text>
        </div>
      ),
    },
    {
      title: "Package",
      key: "package",
      width: 120,
      render: (_: unknown, record: SendcloudShipment) => (
        <div>
          <Text style={{ fontSize: 11 }}>{formatWeightGrams(record.weight)}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 10 }}>
            {formatDimensions(record.length, record.width, record.height)}
          </Text>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} style={{ fontSize: 10 }}>
          {status.replace(/_/g, " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 100,
      render: (date: string) => (
        <Text style={{ fontSize: 11 }}>
          {new Date(date).toLocaleDateString("nl-NL")}
        </Text>
      ),
    },
  ];

  const totalPieces = products.reduce((s, p) => s + (p.pieces || 0), 0);
  const totalCartons = products.reduce((s, p) => s + (p.cartons || 0), 0);
  const totalWeight = Number(
    products.reduce((s, p) => s + (p.gross_weight || 0), 0).toFixed(3),
  );
  const totalCbm = Number(
    products.reduce((s, p) => s + (p.cbm || 0), 0).toFixed(3),
  );
  const { chargeableWeight } = booking
    ? calculateChargeableWeight(booking)
    : { chargeableWeight: 0 };

  const shipmentWeightGrams = shipments.reduce((s, sh) => s + (sh.weight || 0), 0);
  const shipmentLabels = shipments.length;

  return (
    <Modal
      title={kind ? TITLE[kind] : ""}
      open={!!kind}
      onCancel={onClose}
      footer={null}
      width={kind === "shipments" ? 900 : kind === "products" ? 820 : 720}
      destroyOnClose
    >
      {!booking && (
        <Text type="secondary">No booking selected.</Text>
      )}

      {booking && kind === "products" && (
        <>
          <Table
            size="small"
            rowKey={(r) => String(r.id ?? r.name)}
            dataSource={products}
            columns={productColumns}
            pagination={false}
            locale={{ emptyText: "No products on this booking" }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Text strong>Total ({products.length})</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong>{totalPieces}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <Text strong>{totalCartons}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <Text strong>{totalWeight.toFixed(2)} kg</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <Text strong>{totalCbm.toFixed(3)} m³</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <Text strong type="danger">
                    {chargeableWeight.toFixed(2)} kg
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
          <div style={summaryBoxStyle}>
            <Text>
              <Text type="secondary">Products: </Text>
              <Text strong>{products.length}</Text>
            </Text>
            <Text>
              <Text type="secondary">Pieces: </Text>
              <Text strong>{totalPieces}</Text>
            </Text>
            <Text>
              <Text type="secondary">Cartons: </Text>
              <Text strong>{totalCartons}</Text>
            </Text>
            <Text>
              <Text type="secondary">Weight: </Text>
              <Text strong>{totalWeight.toFixed(2)} kg</Text>
            </Text>
            <Text>
              <Text type="secondary">CBM: </Text>
              <Text strong>{totalCbm.toFixed(3)} m³</Text>
            </Text>
            <Text>
              <Text type="secondary">Chargeable: </Text>
              <Text strong type="danger">
                {chargeableWeight.toFixed(2)} kg
              </Text>
              <Tooltip title="Higher of actual weight and volumetric weight (1 CBM = 167 kg)">
                <InfoCircleOutlined
                  style={{ marginLeft: 4, fontSize: 11, color: "rgba(0,0,0,0.45)" }}
                />
              </Tooltip>
            </Text>
          </div>
        </>
      )}

      {booking && kind === "inklaring" && (
        <>
          {missingHsCount > 0 && (
            <Space
              style={{ width: "100%", marginBottom: 8, justify: "flex", justifyContent: "space-between" }}
              wrap
            >
              <Text type="warning" style={{ fontSize: 12 }}>
                {missingHsCount} product{missingHsCount === 1 ? "" : "s"} missing an HS code —
                edit below; Inklaringskosten updates automatically.
              </Text>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={async () => {
                  const text = formatMissingHsCodesForEmail(products, booking);
                  try {
                    await navigator.clipboard.writeText(text);
                    message.success("Copied list for email");
                  } catch {
                    message.error("Could not copy to clipboard");
                  }
                }}
              >
                Copy for email
              </Button>
            </Space>
          )}
          <Table
            size="small"
            rowKey={(r) => String(r.id ?? r.name)}
            dataSource={products}
            columns={productColumns}
            pagination={false}
            locale={{ emptyText: "No products on this booking" }}
          />
          <div style={summaryBoxStyle}>
            <Text>
              <Text type="secondary">Total products: </Text>
              <Text strong>{products.length}</Text>
            </Text>
            <Text>
              <Text type="secondary">Unique HS codes: </Text>
              <Text strong>{uniqueHs}</Text>
            </Text>
            {uniqueHsCodes.length > 0 && (
              <div style={{ width: "100%" }}>
                <Divider style={{ margin: "6px 0" }} />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {uniqueHsCodes.join(" · ")}
                </Text>
              </div>
            )}
          </div>
        </>
      )}

      {booking && kind === "shipments" && (
        <Spin spinning={loadingShipments}>
          <Table
            size="small"
            rowKey="id"
            dataSource={shipments}
            columns={shipmentColumns}
            pagination={false}
            locale={{ emptyText: "No Sendcloud shipments for this booking" }}
          />
          <div style={summaryBoxStyle}>
            <Text>
              <Text type="secondary">Shipment labels: </Text>
              <Text strong>{shipmentLabels}</Text>
            </Text>
            <Text>
              <Text type="secondary">Total weight: </Text>
              <Text strong>{formatWeightGrams(shipmentWeightGrams || null)}</Text>
            </Text>
          </div>
        </Spin>
      )}
    </Modal>
  );
};

export default InvoiceCostOverviewDetails;
